import { type PropsWithChildren, useCallback } from 'react'
import React, { useMemo } from 'react'
import { intl } from 'lang/intl'

import { switchToPanel } from '../../../firebase/hooks/useFloor'
import { setChyron } from '../../../firebase/hooks/useOverlays'
import { setPanelToAuto, usePanel } from '../../../firebase/hooks/usePanel'
import { useLastActiveMediaTrayCard } from '../../../firebase/hooks/useTray'
import type { Mix } from '../../../firebase/model/mix'
import { usePanelLayout } from '../../../Stage/layouts'
import usePlaybackIntent from '../../../Webinar/hooks/usePlaybackIntent'
import { useSortedHumanParticipants } from '../../../webrtc/webRTCRoom'
import { MixPreview } from '../CustomMixes/MixPreview/MixPreview'

import './PanelMonitorContent.scss'

type PanelMonitorContentProps = PropsWithChildren<{
  isSelected?: boolean
}>

const PanelMonitorContent = ({ isSelected }: PanelMonitorContentProps) => {
  const { screen, participantIds, mode } = usePanel()
  const lastActiveMediaTrayCard = useLastActiveMediaTrayCard()
  const { setPlaybackPauseIntent, setPlaybackResumeIntent } =
    usePlaybackIntent()
  const participants = useSortedHumanParticipants()

  const visibleParticipants = useMemo(() => {
    if (mode === 'auto') {
      return participants.map((p) => p.id)
    }

    const participantIdsInRoom = participants.map((p) => p.id)
    return participantIds.filter((i) => participantIdsInRoom.includes(i))
  }, [mode, participantIds, participants])

  const participantCount = visibleParticipants.length

  const panelLayout = usePanelLayout(participantCount, !!screen)

  const mix: Mix = useMemo(() => {
    const m: Mix = {
      name: 'panel',
      id: '0',
      settings: panelLayout.settings,
      feeds: [],
      elements: [],
    }

    let mediaOffset = 0
    panelLayout.elements.forEach((slot, index) => {
      if (slot.type === 'media' && screen) {
        m.feeds.push({
          type: 'media',
          position: slot.position,
          index,
          media:
            screen === 'tray'
              ? { type: 'tray', id: 0 }
              : { type: 'screen', id: screen },
          crop: { zoom: 0, centerY: 0, centerX: 0 },
        })
        mediaOffset += 1
      } else {
        m.feeds.push({
          type: 'camera',
          index,
          position: slot.position,
          media: {
            type: 'camera',
            id: visibleParticipants[index - mediaOffset],
          },
          crop: { zoom: 30, centerY: 0, centerX: 0 },
        })
      }
    })
    m.feeds.reverse()

    return m
  }, [panelLayout, screen, visibleParticipants])

  const handlePanelClick = useCallback(() => {
    switchToPanel()

    if (!screen && participantIds.length === 0) {
      void setPanelToAuto()
    }

    void setChyron(null)
    if (screen !== 'tray') {
      void setPlaybackPauseIntent()
    } else if (lastActiveMediaTrayCard?.type === 'video') {
      void setPlaybackResumeIntent()
    }
  }, [
    lastActiveMediaTrayCard?.type,
    participantIds,
    screen,
    setPlaybackPauseIntent,
    setPlaybackResumeIntent,
  ])

  return (
    <button
      type="button"
      className="panel-monitor-content"
      onClick={handlePanelClick}
      aria-label={
        isSelected
          ? intl.formatMessage({
              defaultMessage: 'The selected speakers panel is shown',
            })
          : intl.formatMessage({
              defaultMessage:
                'Press enter or space to show the selected speakers panel',
            })
      }
    >
      <MixPreview mix={mix} />
    </button>
  )
}

export { PanelMonitorContent }
